<template>
  <div
    ref="headerEl"
    :class="{
      hidden: isFreeze,
      'app-extension-header': appExtensionHeader,
      'hide-right': hideRight,
    }"
    class="header"
  >
    <span
      v-if="titleVisible"
      :class="{
        'disabled-rename': disabledRename,
      }"
      class="title"
      @click.prevent.stop="handleRename"
    >
      {{ headerTitle }}
    </span>
    <div class="actionsBox">
      <div class="actionsBox__inner">
        <div v-if="$slots.headerRight">
          <slot name="headerRight" />
        </div>
        <template v-else>
          <TipButton
            v-if="!isMobile"
            location="bottom"
            :disabled-inject="disabledInject"
            @click="emits('openShare')"
          >
            <template #content>
              {{ t('share') }}
            </template>
            <i class="icon">
              <ShareIcon />
            </i>
          </TipButton>

          <TipButton
            v-if="!isMobile"
            location="bottom"
            :disabled-inject="disabledInject"
            @click="emits('star')"
          >
            <template #content>
              {{ isTagFile ? t('unstar') : t('starred') }}
            </template>
            <i class="icon">
              <StarredIcon v-if="isTagFile" />
              <UnstarIcon v-else />
            </i>
          </TipButton>

          <TipButton
            location="bottom"
            :disabled-inject="disabledInject"
            @click="handleShowMenu"
          >
            <template #content>
              {{ t('more-button') }}
            </template>
            <i class="icon icon-more">
              <MoreIcon />
            </i>
          </TipButton>
        </template>

        <div class="divider" />

        <TipButton
          location="bottom"
          :disabled-inject="disabledInject"
          @click="emits('close')"
        >
          <template #content>
            <Shortcut
              :name="t('close-button')"
              shortcut="[ESC]"
            />
          </template>
          <i class="icon">
            <CloseIcon />
          </i>
        </TipButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { emitFileManagerEvent, FileManagerEvent } from '@pikpak_web_player/components/file-manager/fileManager'
import CloseIcon from '@pikpak_web_player/components/icons/CloseIcon.vue'
import MoreIcon from '@pikpak_web_player/components/icons/MoreIcon.vue'
import ShareIcon from '@pikpak_web_player/components/icons/ShareIcon.vue'
import StarredIcon from '@pikpak_web_player/components/icons/StarredIcon.vue'
import UnstarIcon from '@pikpak_web_player/components/icons/UnstarIcon.vue'
import Shortcut from '@pikpak_web_player/components/tip-button/Shortcut.vue'

import TipButton from '@pikpak_web_player/components/tip-button/TipButton.vue'
import { useCurrentFile, useIsFreeze } from '@pikpak_web_player/hooks'
import { setLocale, SupportLocale, t } from '@pikpak_web_player/locale'
import { HEADER_ITEM_VISIBLE_MIN_WIDTH } from '@pikpak_web_player/utils/constant'
import { useElementSize } from '@vueuse/core'
import { onMounted, ref, watch } from 'vue'

const emits = defineEmits<{
  rename: []
  openShare: []
  star: []
  showMenu: [e: MouseEvent]
  hideMenu: []
  close: []
  onFixStateChange: []
  minimize: []
  maximize: []
}>()

const {
  locale = undefined,
  appExtensionHeader,
  disabledRename,
  disabledInject,
  headerTitle = undefined,
} = defineProps<{
  headerTitle?: string
  isMobile?: boolean
  isTagFile?: boolean
  isMaximize?: boolean
  isFixedWindow?: boolean
  disabledMenu?: boolean
  disabledRename?: boolean
  // 作为 APP Extension 消费页的头部
  appExtensionHeader?: boolean
  locale?: SupportLocale
  disabledInject?: boolean
  hideRight?: boolean
}>()

const isFreeze = disabledInject ? ref(false) : useIsFreeze()
const currentFile = disabledInject ? ref(null) : useCurrentFile()

const headerEl = ref<HTMLElement>()
const titleVisible = ref(true)
const moreVisible = ref(true)

const { width: headerWidth } = useElementSize(headerEl)

const handleShowMenu = (e: MouseEvent) => {
  emits('showMenu', e)

  emitFileManagerEvent(FileManagerEvent.REPORT_CLICK, {
    activeId: currentFile.value?.id || '',
    action: 'more',
    trigger: 'shortcut',
  })
}

const handleRename = () => {
  if (disabledRename) return
  emits('rename')
}

watch(headerWidth, w => {
  titleVisible.value = w > HEADER_ITEM_VISIBLE_MIN_WIDTH
  moreVisible.value = w > HEADER_ITEM_VISIBLE_MIN_WIDTH
})

watch(moreVisible, v => {
  if (!v) {
    emits('hideMenu')
  }
})

watch(
  () => locale,
  val => {
    if (val) {
      setLocale(val, false)
    }
  },
)

onMounted(() => {
  if (locale) {
    setLocale(locale, false)
  }
})

defineExpose({
  headerEl,
})
</script>

<style lang="less" scoped>
.header {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 5;
  padding: 0 24px;
  box-sizing: border-box;

  width: 100%;
  height: var(--header-bar-height);
  transform: translate(0, -100%);
  transition: transform 0.3s;
  background: var(--fm-header-bg);

  display: flex;
  align-items: center;

  &.hidden {
    visibility: hidden;
  }

  &.app-extension-header {
    transform: unset;
    transition: unset;
    background: unset;
    position: unset;
    height: 72px;
  }

  &.hide-right {
    .actionsBox {
      visibility: hidden;
      pointer-events: none;
    }
  }

  .title {
    color: var(--text-normal-color);
    font-size: 16px;
    font-weight: 600;
    line-height: 26px;
    height: 26px;
    padding-right: 32px;

    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .title.disabled-rename {
    cursor: default;
  }

  .actionsBox {
    margin-left: auto;
    pointer-events: all;

    &__inner {
      display: flex;
      gap: 24px;
      align-items: center;
    }

    .divider {
      width: 1px;
      height: 16px;
      background: rgba(255, 255, 255, 0.4);
    }

    .icon {
      width: 24px;
      height: 24px;
      cursor: pointer;
      color: var(--text-normal-color);
      transition: color 0.3s;

      &:hover {
        color: var(--button-hover-color);
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .header {
    &.app-extension-header {
      height: 58px;
    }
  }
}
</style>
